import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useLanguage from '../hooks/useLanguage';
import productsData from '../data/products.json';
import translationsData from '../data/translations.json';
import Button from '../components/common/Button';
import * as FiIcons from 'react-icons/fi';

/**
 * ProductDetail Page
 * Displays detailed information about a specific product
 */
const ProductDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Find product by ID
  const product = productsData.products.find((p) => p.id === id);

  // Get translations
  const contactBtn = language === 'ko' ? translationsData.buttons.contact : translationsData.buttons.contactEn;
  const backToListBtn = language === 'ko' ? translationsData.buttons.backToList : translationsData.buttons.backToListEn;
  const specificationsTitle = language === 'ko' ? translationsData.titles.specifications : translationsData.titles.specificationsEn;
  const featuresTitle = language === 'ko' ? translationsData.titles.features : translationsData.titles.featuresEn;
  const applicationsTitle = language === 'ko' ? translationsData.titles.applications : translationsData.titles.applicationsEn;
  const productNotFoundMsg = language === 'ko' ? translationsData.messages.productNotFound : translationsData.messages.productNotFoundEn;

  // If product not found
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-16">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{productNotFoundMsg}</h1>
          <p className="text-gray-600 mb-8">
            {language === 'ko'
              ? '요청하신 제품을 찾을 수 없습니다. 제품 목록으로 돌아가주세요.'
              : 'The product you requested could not be found. Please return to the product list.'}
          </p>
          <Link to="/products">
            <Button variant="primary" size="lg">{backToListBtn}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get product data based on language
  const productName = language === 'ko' ? product.name : product.nameEn;
  const productCategory = language === 'ko' ? product.category : product.categoryEn;
  const detailDescription = language === 'ko' ? product.detailDescription : product.detailDescriptionEn;
  const applications = language === 'ko' ? product.applications : product.applicationsEn;

  // Dynamic icon rendering
  const getIcon = (iconName) => {
    const Icon = FiIcons[iconName];
    return Icon ? <Icon className="w-8 h-8 text-blue-600" /> : <FiIcons.FiBox className="w-8 h-8 text-blue-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/products')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-8 transition-colors"
        >
          <FiIcons.FiArrowLeft className="mr-2" />
          {backToListBtn}
        </button>

        {/* Product Main Content */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Left Column - Image */}
            <div>
              <img
                src={product.image}
                alt={productName}
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>

            {/* Right Column - Product Info */}
            <div>
              {/* Category Badge */}
              <div className="inline-block bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                {productCategory}
              </div>

              {/* Product Name */}
              <h1 className="text-4xl font-bold text-gray-900 mb-6">{productName}</h1>

              {/* Detail Description */}
              <p className="text-gray-700 leading-relaxed mb-8 text-lg">
                {detailDescription}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="flex-1">
                  <Button variant="primary" size="lg" className="w-full">
                    {contactBtn}
                  </Button>
                </Link>
                <Link to="/products" className="flex-1">
                  <Button variant="outline" size="lg" className="w-full">
                    {backToListBtn}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {specificationsTitle}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <tbody>
                {product.specs.map((spec, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="border border-gray-200 px-6 py-4 font-semibold text-gray-900 w-1/3">
                      {language === 'ko' ? spec.label : spec.labelEn}
                    </td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">
                      {language === 'ko' ? spec.value : spec.valueEn}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {featuresTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-start p-6 bg-gray-50 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 mr-4">
                  {getIcon(feature.icon)}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'ko' ? feature.title : feature.titleEn}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {language === 'ko' ? feature.description : feature.descriptionEn}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Applications Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {applicationsTitle}
          </h2>
          <div className="flex flex-wrap gap-3">
            {applications.map((application, index) => (
              <span
                key={index}
                className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium"
              >
                {application}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
